<template>
    <div>
        <div class="panel">
            <div class="panel-heading">
                <div class="panel-label">
                    <h3>{{$t('PROMOTIONS.title')}}</h3>
                </div>
                <div class="panel-toolbar" v-if="edit && $isAllowed('edit')">
                    <cph-button @clicked="isFormOpen = true"
                                :buttonText="'Add promotions'"
                                :classList="'btn btn-cph-primary'">
                    </cph-button>
                </div>
            </div>
            <div class="panel-body">
                <div class="row" slot="body">
                    <div class="col-12">
                        <!--ACTIVE PROMOTIONS HERE-->
                        <active-regular-promotions :promotions="regularPromotions"
                                                    @product="openForm($event)" :symbol="symbol" :fractionalDigits="fractionalDigits"
                                                    @addFunctionToSave="addFunctionToSave" @saveFunctions="saveFunctions"
                                                    :decimalSeparator="decimalSeparator" :separator="separator"
                                                    @showInformationUpdateCubes="$emit('showInformationUpdateCubes')" />
                    </div>

                    <div class="col-12">
                        <!--HISTORY PROMOTIONS HERE-->
                        <active-regular-promotions :promotions="historyOfRegularPromotions"
                                                    @product="openForm($event)" :symbol="symbol" :history="true" :fractionalDigits="fractionalDigits"
                                                    @addFunctionToSave="addFunctionToSave" @saveFunctions="saveFunctions"
                                                    :decimalSeparator="decimalSeparator" :separator="separator"
                                                    @showInformationUpdateCubes="$emit('showInformationUpdateCubes')" />
                    </div>

                    <div class="col-12">
                        <!--BULK PRICE LIST-->
                        <active-bulk-promotions :promotions="bulkPromotions"
                                                @product="openForm($event)" :symbol="symbol" :fractionalDigits="fractionalDigits"
                                                @addFunctionToSave="addFunctionToSave" @saveFunctions="saveFunctions"
                                                :decimalSeparator="decimalSeparator" :separator="separator"
                                                @showInformationUpdateCubes="$emit('showInformationUpdateCubes')" />
                    </div>

                    <div class="col-12">
                        <!--FEE ORDER-->
                        <h4>{{$t('PROMOTIONS.productFee')}}</h4>
                        <fee-table v-if="getFees.length > 0" :fees="getFees" @feeTableClicked="feeTableClicked" :symbol="symbol" :fractionalDigits="fractionalDigits"
                            @addFunctionToSave="addFunctionToSave" @saveFunctions="saveFunctions"
                            :decimalSeparator="decimalSeparator" :separator="separator"
                            @showInformationUpdateCubes="$emit('showInformationUpdateCubes')"
                            />
                        <p v-else>No fee</p>
                    </div>
                </div>
            </div>
        </div>

        <transition name="fade" mode="out-in" v-if="$isAllowed('edit')">
            <functional-modal
                @close="closeForm"
                @save="saveButton"
                key="promotion-modal"
                :showModal="isFormOpen"
                :showSave="true"
                :big="true"
                :title="formTitle"
                closeText="UI.cancel"
                saveText="UI.add"
                v-if="isFormOpen"
            >
                <div :class="['col-12']" slot="body">
                    <promotion-form
                        :products="products"
                        :editedProduct="currentProduct"
                        :prices="prices"
                        @addFunctionToSave="addFunctionToSave"
                        @removeFromStack="removeFromStack"
                        :showadd="showadd"
                        :fractionalDigits="fractionalDigits"
                        :decimalSeparator="decimalSeparator"
                        :separator="separator"
                        :symbol="symbol"
                    />
                </div>
            </functional-modal>
        </transition>
    </div>
</template>

<script type="text/babel">
import {mapGetters, mapMutations, mapActions} from 'vuex';

import Filters from '../../../mixins/CphFilters.vue';
import CphWidget from '../../../components/Shared/CphWidget.vue';
import {PromotionsPerimeter} from '../../../router/perimeters/promotionsPerimeter';
import ActiveRegularPromotions from '../../../components/Panel/Promotions/ActiveRegularPromotions';
import ActiveBulkPromotions from '../../../components/Panel/Promotions/ActiveBulkPromotions';
import CphButton from '../../../components/Shared/UI/Button/CphButton';
import FunctionalModal from '../../../components/Shared/Modal/FunctionalModal.vue';
import PromotionForm from '../../../components/Panel/Promotions/PromotionForm';
import PromotionMixin from '../../../components/Panel/Promotions/PromotionMixin';
import FeeTable from '../Fee/FeeTable.vue';

export default {
    name: 'PromotionsView',
    props:{
        edit:{
            type: Boolean,
            default: false
        },
        formOpen: {
            type: Boolean,
            default: false,
        },
        selectedProduct: {
            type: Object,
            default: null
        },
        orderFee: {
            type: Object,
            default: null,
        },
        showadd: {
            type: Boolean,
            default: true,
        }
    },
    data(){
        return {
            isFormOpen: this.formOpen,
            currentProduct: this.selectedProduct,
            funcStack: {},
            symbol: '',
            fractionalDigits: 2,
            decimalSeparator: '.',
            separator: ","
        }
    },
    watch: {
        formOpen(newVal, oldVal){
            this.isFormOpen = newVal;
        },
        selectedProduct(newVal, oldVal) {
            this.currentProduct = newVal;
        },
    },
    perimeters: [PromotionsPerimeter],
    mixins: [Filters, PromotionMixin],
    computed: {
        ...mapGetters({
            products: 'getProducts',
            prices: 'getAllPrices',
            promotions: 'getPromotions',
            getProductsById: 'getProductsById',
            getFees: 'getFees',
            getActiveShop: 'getActiveShop',
        }),
        formTitle () {
            return this.$t('PROMOTIONS.manage');
        },
        activeOrUpcomingPromotions () {
            return this.promotions.filter(promotion => !this.hasFinished(promotion));
        },
        finishedPromotions () {
            return this.promotions.filter(promotion => this.hasFinished(promotion));
        },
        bulkPromotions () {
            return this.promotions.filter(({type}) => type === 'bulk');
        },
        regularPromotions () {
            return this.activeOrUpcomingPromotions.filter(({type}) => type === 'period');
        },
        historyOfRegularPromotions () {
            return this.finishedPromotions.filter(({type}) => type === 'period');
        }
    },
    methods: {
        ...mapActions([
            'productsSaved',
            'GetShopById'
        ]),
        ...mapGetters([ 'currentShop']),
        feeTableClicked (data) {
            if(this.$route.name === 'FastEdit') {
                this.$emit('feeTableClicked', data)
            }
        },
        async openForm (product) {
            if(this.$route.name === 'FastEdit') { // work only in fastedit page
                if (this.$isNotAllowed('edit')) {
                    return;
                }
                // let productById = await this.getProductsById(product)[0];
                this.isFormOpen = true;
                this.currentProduct = productById;
            }
        },
        async closeForm () {
            await this.$store.dispatch('getPromotionsAsync');
            this.isFormOpen = false;
            this.$emit('closeModal');
            this.funcStack = {}
        },
        addFunctionToSave(func) {
            this.funcStack[func.id] = func.run;
            // console.log({...this.funcStack})
        },
        removeFromStack({type}){
            // console.log("removeFromStack", {type})
            // console.log({...this.funcStack})
        },
        saveFunctions (){
            Object.values(this.funcStack).forEach(async (fnc) => {
                try {
                    await fnc();
                } catch (error) {
                    throw error;
                }
            })
            this.funcStack = {}
            return Promise.resolve()
        },
        saveButton(){
            this.saveFunctions()
            .then(() => {
                this.$emit('showInformationUpdateCubes')
                this.closeForm()
            })
            .catch(err => {
                this.closeForm()
            })
        }
    },
    components: {
        PromotionForm,
        ActiveBulkPromotions,
        ActiveRegularPromotions,
        CphWidget,
        CphButton,
        FunctionalModal,
        FeeTable
    },
    async created () {
        let context = this.$route.params.id;
        await this.$store.dispatch('setContext', context);
        await this.$store.dispatch('getPromotionsAsync');
        await this.$store.dispatch('getFees');
        await this.$store.dispatch('getProductDefaultListAsync');

        if(!this.currentShop.currency){
            const shop = await this.GetShopById(context);
            const numberFormat = new Intl.NumberFormat('en', { style: 'currency', currency: shop?.currency?.code || "EUR" }).formatToParts(10000.10);
            this.symbol = numberFormat.find(x => x.type === 'currency').value;
            this.decimalSeparator = numberFormat.find(x => x.type === 'decimal').value;
            this.separator = numberFormat.find(x => x.type === 'group').value;
            this.fractionalDigits = shop?.currency?.fractionDigits || 2;
        }
    }
};

</script>

<style lang="scss">
</style>
